// frontend/src/components/superadmin/AIUsageTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Cell, CartesianGrid,
} from 'recharts';

// ── Constants ────────────────────────────────────────────────
const MODULE_COLORS = {
  core:           '#8e44ad',
  bookings:       '#E64C31',
  communications: '#3498db',
  loyalty:        '#f7931e',
  reports:        '#00d4aa',
  customerPortal: '#16a085',
};

const MODULE_LABELS = {
  core:           'Core AI',
  bookings:       'Bookings AI',
  communications: 'Communications AI',
  loyalty:        'Loyalty AI',
  reports:        'Reports AI',
  customerPortal: 'Customer Portal AI',
};

// Narrower names for the per-salon table, where every module gets its own column.
const MODULE_SHORT_LABELS = {
  core:           'Core AI',
  bookings:       'Bookings AI',
  communications: 'Comms AI',
  loyalty:        'Loyalty AI',
  reports:        'Reports AI',
  customerPortal: 'Portal AI',
};

const MODULE_KEYS = Object.keys(MODULE_LABELS);

// ── Helpers ──────────────────────────────────────────────────
function formatUSD(amount) {
  const n = amount || 0;
  if (n === 0) return '$0.0000';
  return `$${n.toFixed(4)}`;
}

function formatTokens(n) {
  if (!n) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function getLastNMonths(n) {
  const months = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return months;
}

function ymToLabel(ym) {
  const [year, month] = ym.split('-');
  return new Date(parseInt(year), parseInt(month) - 1, 1)
    .toLocaleDateString('en-SG', { month: 'short', year: '2-digit' });
}

// ── Component ────────────────────────────────────────────────
export default function AIUsageTab({ clients = [] }) {
  const [periodMonths, setPeriodMonths] = useState(1);
  const [allSummaries, setAllSummaries] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');

  const fetchSummaries = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const snap = await getDocs(collection(db, 'aiUsageSummary'));
      setAllSummaries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      setError('Failed to load AI usage data. Please try again.');
      console.error('AIUsageTab fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSummaries(); }, [fetchSummaries]);

  // ── Filter to selected period ──────────────────────────────
  const activeMonths = getLastNMonths(periodMonths);
  const filtered     = allSummaries.filter(s => activeMonths.includes(s.yearMonth));

  // ── Platform-wide totals ───────────────────────────────────
  const platformTotals = filtered.reduce(
    (acc, s) => {
      acc.totalTokens    += s.totalTokens         || 0;
      acc.rawCostUSD     += s.totalRawCostUSD     || 0;
      acc.chargedCostUSD += s.totalChargedCostUSD || 0;
      acc.requestCount   += s.requestCount        || 0;
      return acc;
    },
    { totalTokens: 0, rawCostUSD: 0, chargedCostUSD: 0, requestCount: 0 }
  );
  const markupRevenue = platformTotals.chargedCostUSD - platformTotals.rawCostUSD;

  // ── Per-salon aggregation ──────────────────────────────────
  const bySalon = {};
  filtered.forEach(s => {
    if (!bySalon[s.salonId]) {
      bySalon[s.salonId] = {
        salonId:       s.salonId,
        byModule:      {},
        totalTokens:    0,
        rawCostUSD:     0,
        chargedCostUSD: 0,
        requestCount:   0,
      };
    }
    const salon = bySalon[s.salonId];
    salon.totalTokens    += s.totalTokens         || 0;
    salon.rawCostUSD     += s.totalRawCostUSD     || 0;
    salon.chargedCostUSD += s.totalChargedCostUSD || 0;
    salon.requestCount   += s.requestCount        || 0;

    Object.entries(s.byModule || {}).forEach(([mod, data]) => {
      if (!salon.byModule[mod]) {
        salon.byModule[mod] = { totalTokens: 0, chargedCostUSD: 0, requestCount: 0 };
      }
      salon.byModule[mod].totalTokens    += data.totalTokens    || 0;
      salon.byModule[mod].chargedCostUSD += data.chargedCostUSD || 0;
      salon.byModule[mod].requestCount   += data.requestCount   || 0;
    });
  });

  const salonRows = Object.values(bySalon)
    .sort((a, b) => b.chargedCostUSD - a.chargedCostUSD);

  // Client name lookup
  const clientNameMap = {};
  clients.forEach(c => { clientNameMap[c.salonId || c.id] = c.name; });

  // ── Module bar chart (platform-wide) ──────────────────────
  const moduleChartData = MODULE_KEYS.map(mod => {
    const tokens   = filtered.reduce((sum, s) => sum + (s.byModule?.[mod]?.totalTokens    || 0), 0);
    const cost     = filtered.reduce((sum, s) => sum + (s.byModule?.[mod]?.chargedCostUSD || 0), 0);
    const requests = filtered.reduce((sum, s) => sum + (s.byModule?.[mod]?.requestCount   || 0), 0);
    return { name: MODULE_LABELS[mod], mod, tokens, cost: parseFloat(cost.toFixed(4)), requests };
  }).filter(d => d.tokens > 0);

  // ── Monthly cost trend ─────────────────────────────────────
  const trendMonths = getLastNMonths(Math.max(periodMonths, 2));
  const monthlyTrend = trendMonths.map(ym => {
    const docs  = allSummaries.filter(s => s.yearMonth === ym);
    const cost   = docs.reduce((sum, s) => sum + (s.totalChargedCostUSD || 0), 0);
    const tokens = docs.reduce((sum, s) => sum + (s.totalTokens         || 0), 0);
    return { month: ymToLabel(ym), cost: parseFloat(cost.toFixed(4)), tokens };
  });

  const periodLabel = periodMonths === 1
    ? 'This Month'
    : `Last ${periodMonths} Months`;

  // ── Render ─────────────────────────────────────────────────
  if (loading) {
    return <div className="sa-ai-state">Loading AI usage data…</div>;
  }
  if (error) {
    return (
      <div className="sa-ai-state sa-ai-state--error">
        {error}
        <button className="sa-ai-retry-btn" onClick={fetchSummaries}>Retry</button>
      </div>
    );
  }

  return (
    <div className="sa-ai-usage-tab">

      {/* ── Period selector ────────────────────────────────── */}
      <div className="sa-ai-period-bar">
        <span className="sa-ai-period-label">Period:</span>
        {[
          { label: 'This Month',     value: 1  },
          { label: 'Last 3 Months',  value: 3  },
          { label: 'Last 6 Months',  value: 6  },
          { label: 'Last 12 Months', value: 12 },
        ].map(opt => (
          <button
            key={opt.value}
            className={`sa-ai-period-btn${periodMonths === opt.value ? ' active' : ''}`}
            onClick={() => setPeriodMonths(opt.value)}
          >
            {opt.label}
          </button>
        ))}
        <button className="sa-ai-refresh-btn" onClick={fetchSummaries} title="Refresh data">
          ↻ Refresh
        </button>
      </div>

      {/* ── Summary cards ──────────────────────────────────── */}
      <section className="sa-section">
        <h2 className="sa-section-title">Platform AI Spend — {periodLabel}</h2>
        <div className="sa-ai-summary-cards">
          <div className="sa-ai-card">
            <span className="sa-ai-card-value">{formatTokens(platformTotals.totalTokens)}</span>
            <span className="sa-ai-card-label">Total Tokens</span>
            <span className="sa-ai-card-sub">{platformTotals.requestCount.toLocaleString()} requests</span>
          </div>
          <div className="sa-ai-card">
            <span className="sa-ai-card-value">{formatUSD(platformTotals.rawCostUSD)}</span>
            <span className="sa-ai-card-label">API Cost (USD)</span>
            <span className="sa-ai-card-sub">At Anthropic rates</span>
          </div>
          <div className="sa-ai-card sa-ai-card--billed">
            <span className="sa-ai-card-value">{formatUSD(platformTotals.chargedCostUSD)}</span>
            <span className="sa-ai-card-label">Billed to Salons</span>
            <span className="sa-ai-card-sub">Inc. 20% markup</span>
          </div>
          <div className="sa-ai-card sa-ai-card--markup">
            <span className="sa-ai-card-value">{formatUSD(markupRevenue)}</span>
            <span className="sa-ai-card-label">Markup Revenue</span>
            <span className="sa-ai-card-sub">20% margin on API cost</span>
          </div>
        </div>
      </section>

      {/* ── Charts ─────────────────────────────────────────── */}
      <div className="sa-ai-charts-row">

        {/* Module usage bar chart */}
        {moduleChartData.length > 0 && (
          <section className="sa-section sa-ai-chart-section">
            <h2 className="sa-section-title">Usage by Module</h2>
            <p className="sa-section-sub">Tokens consumed per AI module — {periodLabel}</p>
            <div className="sa-chart-card">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={moduleChartData} margin={{ top: 8, right: 32, left: 0, bottom: 8 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={v => formatTokens(v)} tick={{ fontSize: 11 }} width={52} />
                  <Tooltip
                    formatter={(value, name) =>
                      [formatTokens(value), 'Tokens']
                    }
                  />
                  <Bar dataKey="tokens" radius={[4, 4, 0, 0]}>
                    {moduleChartData.map((entry) => (
                      <Cell key={entry.mod} fill={MODULE_COLORS[entry.mod] || '#E64C31'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>
        )}

        {/* Monthly cost trend (only when viewing > 1 month) */}
        {periodMonths > 1 && (
          <section className="sa-section sa-ai-chart-section">
            <h2 className="sa-section-title">Monthly Cost Trend</h2>
            <p className="sa-section-sub">Billed amount per month (USD, inc. 20% markup)</p>
            <div className="sa-chart-card">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={monthlyTrend} margin={{ top: 8, right: 24, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={v => `$${v.toFixed(2)}`} tick={{ fontSize: 11 }} width={60} />
                  <Tooltip formatter={v => [`$${v.toFixed(4)}`, 'Billed (USD)']} />
                  <Line
                    type="monotone"
                    dataKey="cost"
                    name="Billed (USD)"
                    stroke="#E64C31"
                    strokeWidth={2.5}
                    dot={{ fill: '#E64C31', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>
        )}
      </div>

      {/* ── Per-salon breakdown table ───────────────────────── */}
      <section className="sa-section">
        <h2 className="sa-section-title">Usage by Salon</h2>

        {salonRows.length === 0 ? (
          <p className="sa-ai-empty">
            No AI usage recorded for this period. Usage will appear here once salons start using AI modules.
          </p>
        ) : (
          <div className="sa-ai-table-wrap">
            <table className="sa-ai-table">
              <thead>
                <tr>
                  <th>Salon</th>
                  <th>Salon ID</th>
                  {MODULE_KEYS.map(mod => (
                    <th key={mod}>{MODULE_SHORT_LABELS[mod] || MODULE_LABELS[mod]}</th>
                  ))}
                  <th>Total Tokens</th>
                  <th>Requests</th>
                  <th>API Cost</th>
                  <th>Billed (USD)</th>
                </tr>
              </thead>
              <tbody>
                {salonRows.map(salon => (
                  <tr key={salon.salonId}>
                    <td className="sa-ai-salon-name">
                      {clientNameMap[salon.salonId] || <span className="sa-ai-unknown">Unknown</span>}
                    </td>
                    <td className="sa-ai-salon-id">{salon.salonId}</td>
                    {MODULE_KEYS.map(mod => (
                      <td key={mod} className="sa-ai-mod-cell">
                        {salon.byModule[mod] ? (
                          <span className="sa-ai-mod-inner">
                            <span className="sa-ai-mod-tokens">{formatTokens(salon.byModule[mod].totalTokens)}</span>
                            <span className="sa-ai-mod-cost">{formatUSD(salon.byModule[mod].chargedCostUSD)}</span>
                          </span>
                        ) : (
                          <span className="sa-ai-mod-nil">—</span>
                        )}
                      </td>
                    ))}
                    <td className="sa-ai-total-tokens">{formatTokens(salon.totalTokens)}</td>
                    <td>{salon.requestCount}</td>
                    <td className="sa-ai-raw-cost">{formatUSD(salon.rawCostUSD)}</td>
                    <td className="sa-ai-billed-cell">
                      <span className="sa-ai-billed-badge">{formatUSD(salon.chargedCostUSD)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="sa-ai-table-total">
                  <td colSpan={2 + MODULE_KEYS.length}><strong>Platform Total</strong></td>
                  <td><strong>{formatTokens(platformTotals.totalTokens)}</strong></td>
                  <td><strong>{platformTotals.requestCount}</strong></td>
                  <td><strong>{formatUSD(platformTotals.rawCostUSD)}</strong></td>
                  <td><strong>{formatUSD(platformTotals.chargedCostUSD)}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>

    </div>
  );
}
